import { sectionNames } from "@saleor/intl";
import { getQueryParamString } from "@saleor/utils/urls";
import { parse as parseQs } from "qs";
import React from "react";
import { useIntl } from "react-intl";
import { Route, Routes, useLocation, useParams } from "react-router-dom";

import { WindowTitle } from "../components/WindowTitle";
import { LanguageCodeEnum } from "../types/globalTypes";
import {
  languageEntitiesPath,
  languageEntityPath,
  languageListPath,
  TranslatableEntities
} from "./urls";
import TranslationsCategoriesComponent, {
  TranslationsCategoriesQueryParams
} from "./views/TranslationsCategories";
import TranslationsCollectionsComponent, {
  TranslationsCollectionsQueryParams
} from "./views/TranslationsCollections";
import TranslationsEntitiesComponent from "./views/TranslationsEntities";
import TranslationsLanguageList from "./views/TranslationsLanguageList";
import TranslationsPagesComponent, {
  TranslationsPagesQueryParams
} from "./views/TranslationsPages";
import TranslationsProductsComponent, {
  TranslationsProductsQueryParams
} from "./views/TranslationsProducts";
import TranslationsProductTypesComponent, {
  TranslationsProductTypesQueryParams
} from "./views/TranslationsProductTypes";
import TranslationsSaleComponent, {
  TranslationsSalesQueryParams
} from "./views/TranslationsSales";
import TranslationsVouchersComponent, {
  TranslationsVouchersQueryParams
} from "./views/TranslationsVouchers";

const TranslationsEntities: React.FC = () => {
  const location = useLocation();
  const { languageCode } = useParams();
  const qs = parseQs(location.search.substr(1));

  return <TranslationsEntitiesComponent language={languageCode} params={qs} />;
};

const TranslationsCategories: React.FC = () => {
  const location = useLocation();
  const { id, languageCode } = useParams();
  const qs = parseQs(location.search.substr(1));
  const params: TranslationsCategoriesQueryParams = {
    activeField: getQueryParamString(qs.activeField)
  };
  return (
    <TranslationsCategoriesComponent
      id={decodeURIComponent(id)}
      languageCode={LanguageCodeEnum[languageCode]}
      params={params}
    />
  );
};
const TranslationsCollections: React.FC = () => {
  const location = useLocation();
  const { id, languageCode } = useParams();
  const qs = parseQs(location.search.substr(1));
  const params: TranslationsCollectionsQueryParams = {
    activeField: getQueryParamString(qs.activeField)
  };
  return (
    <TranslationsCollectionsComponent
      id={decodeURIComponent(id)}
      languageCode={LanguageCodeEnum[languageCode]}
      params={params}
    />
  );
};
const TranslationsProducts: React.FC = () => {
  const location = useLocation();
  const { id, languageCode } = useParams();
  const qs = parseQs(location.search.substr(1));
  const params: TranslationsProductsQueryParams = {
    activeField: getQueryParamString(qs.activeField)
  };
  return (
    <TranslationsProductsComponent
      id={decodeURIComponent(id)}
      languageCode={LanguageCodeEnum[languageCode]}
      params={params}
    />
  );
};
const TranslationsSales: React.FC = () => {
  const location = useLocation();
  const { id, languageCode } = useParams();
  const qs = parseQs(location.search.substr(1));
  const params: TranslationsSalesQueryParams = {
    activeField: getQueryParamString(qs.activeField)
  };
  return (
    <TranslationsSaleComponent
      id={decodeURIComponent(id)}
      languageCode={LanguageCodeEnum[languageCode]}
      params={params}
    />
  );
};
const TranslationsVouchers: React.FC = () => {
  const location = useLocation();
  const { id, languageCode } = useParams();
  const qs = parseQs(location.search.substr(1));
  const params: TranslationsVouchersQueryParams = {
    activeField: getQueryParamString(qs.activeField)
  };
  return (
    <TranslationsVouchersComponent
      id={decodeURIComponent(id)}
      languageCode={LanguageCodeEnum[languageCode]}
      params={params}
    />
  );
};
const TranslationsPages: React.FC = () => {
  const location = useLocation();
  const { id, languageCode } = useParams();
  const qs = parseQs(location.search.substr(1));
  const params: TranslationsPagesQueryParams = {
    activeField: getQueryParamString(qs.activeField)
  };
  return (
    <TranslationsPagesComponent
      id={decodeURIComponent(id)}
      languageCode={LanguageCodeEnum[languageCode]}
      params={params}
    />
  );
};
const TranslationsProductTypes: React.FC = () => {
  const location = useLocation();
  const { id, languageCode } = useParams();
  const qs = parseQs(location.search.substr(1));
  const params: TranslationsProductTypesQueryParams = {
    activeField: getQueryParamString(qs.activeField)
  };
  return (
    <TranslationsProductTypesComponent
      id={decodeURIComponent(id)}
      languageCode={LanguageCodeEnum[languageCode]}
      params={params}
    />
  );
};

const TranslationsRouter: React.FC = () => {
  const intl = useIntl();

  return (
    <>
      <WindowTitle title={intl.formatMessage(sectionNames.translations)} />
      <Routes>
        <Route path={languageListPath} element={<TranslationsLanguageList />} />
        <Route
          path={languageEntitiesPath(":languageCode")}
          element={<TranslationsEntities />}
        />
        <Route
          path={languageEntityPath(
            ":languageCode",
            TranslatableEntities.products,
            ":id"
          )}
          element={<TranslationsProducts />}
        />
        <Route
          path={languageEntityPath(
            ":languageCode",
            TranslatableEntities.categories,
            ":id"
          )}
          element={<TranslationsCategories />}
        />
        <Route
          path={languageEntityPath(
            ":languageCode",
            TranslatableEntities.collections,
            ":id"
          )}
          element={<TranslationsCollections />}
        />
        <Route
          path={languageEntityPath(
            ":languageCode",
            TranslatableEntities.sales,
            ":id"
          )}
          element={<TranslationsSales />}
        />
        <Route
          path={languageEntityPath(
            ":languageCode",
            TranslatableEntities.vouchers,
            ":id"
          )}
          element={<TranslationsVouchers />}
        />
        <Route
          path={languageEntityPath(
            ":languageCode",
            TranslatableEntities.pages,
            ":id"
          )}
          element={<TranslationsPages />}
        />
        <Route
          path={languageEntityPath(
            ":languageCode",
            TranslatableEntities.productTypes,
            ":id"
          )}
          element={<TranslationsProductTypes />}
        />
      </Routes>
    </>
  );
};
TranslationsRouter.displayName = "TranslationsRouter";
export default TranslationsRouter;
